import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useRefresh } from "./RefreshContext";

/**
 * Sophie chat session state.
 *
 * The conversation is held client-side only (cleared on refresh / logout) —
 * no vector memory, no persistence, per the spec. The backend is stateless
 * across requests and rebuilds the system prompt every turn.
 *
 * `messages` includes ALL roles (user, assistant, tool) so we can replay the
 * full history to the backend on the next request. The UI filters out
 * tool/tool_calls when rendering.
 */
const SophieContext = createContext(null);

const WELCOME = {
  role: "assistant",
  content:
    "Hi, I'm Sophie. I can book, cancel, reschedule, or look up appointments for you. Tell me what you need.",
};

export function SophieProvider({ children }) {
  const [messages, setMessages] = useState([WELCOME]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inflight = useRef(false);
  const messagesRef = useRef([WELCOME]);
  const { bumpBookings } = useRefresh();

  // Keep ref in sync with state so async handlers always see the latest history.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || inflight.current) return;
      inflight.current = true;
      setLoading(true);

      const userMsg = { role: "user", content: trimmed };
      const nextHistory = [...messagesRef.current, userMsg];
      messagesRef.current = nextHistory;
      setMessages(nextHistory);

      // Strip the local "welcome" assistant message before sending: the
      // backend rebuilds the system prompt itself, and our welcome was UI-only.
      const historyForServer = nextHistory.filter(
        (m, i) => !(i === 0 && m === WELCOME)
      );

      try {
        const { data } = await api.post("/sophie/chat", {
          messages: historyForServer,
        });
        const appended = data.messages || [];
        const updated = [...nextHistory, ...appended];
        messagesRef.current = updated;
        setMessages(updated);
        if (data.booking_changed) bumpBookings();
      } catch (e) {
        const errMsg = {
          role: "assistant",
          content: `I couldn't reach the server: ${formatApiError(e.response?.data?.detail) || e.message}`,
        };
        const updated = [...nextHistory, errMsg];
        messagesRef.current = updated;
        setMessages(updated);
      } finally {
        inflight.current = false;
        setLoading(false);
      }
    },
    [bumpBookings]
  );

  const reset = useCallback(() => {
    messagesRef.current = [WELCOME];
    setMessages([WELCOME]);
  }, []);

  return (
    <SophieContext.Provider
      value={{ messages, loading, open, setOpen, sendMessage, reset }}
    >
      {children}
    </SophieContext.Provider>
  );
}

export function useSophie() {
  const ctx = useContext(SophieContext);
  if (!ctx) throw new Error("useSophie must be used within SophieProvider");
  return ctx;
}
